/**
 * WHAT: Location Page JavaScript
 * WHY: Handle location page functionality and data management
 * HOW: ES6 modules with async/await pattern
 * IMPORTANCE: Dynamic location display and user interaction
 */

import { CONFIG } from './config/config.js';
import DOMPurify from 'dompurify';

/**
 * WHAT: Location Page Manager
 * WHY: Centralized location page functionality
 * HOW: Class-based organization with modular methods
 * IMPORTANCE: Maintainable and scalable code structure
 */
class LocationPageManager {
    constructor() {
        this.locationData = null;
        this.backgroundImages = [];
        this.currentBackgroundIndex = 0;
        this.backgroundInterval = null;
        this.currentFilter = 'all';
        this.selectedLocation = null;
        this.map = null;
        this.markers = [];
        /** @type {{ hoursMode?: string, hoursDataSource?: string, lastSquareHoursSyncedAt?: string } | null} */
        this.hoursMeta = null;

        this.init();
    }

    /**
     * WHAT: Initialize location page
     * WHY: Set up all page functionality
     * HOW: Call initialization methods in sequence
     * IMPORTANCE: Proper page setup
     */
    async init() {
        try {
            await Promise.all([this.loadLocationData(), this.loadHoursDisplayMeta()]);
            // Location page background now comes from shared body::before + background-rotator.js.
            // Keep a single background layer to avoid double-fade artifacts.
            this.setupEventListeners();
            this.renderOverviewCards();
            this.setupLocationSelector();
            this.setupMap();
            this.renderLocationGrid();
            this.updateBusinessInfo();
        } catch (error) {
            console.error('Error initializing location page:', error);
            this.showError('Failed to load location data. Please try again later.');
        }
    }

    /**
     * WHAT: Load location data from API
     * WHY: Get current location information
     * HOW: Fetch from backend API
     * IMPORTANCE: Dynamic content loading
     */
    async loadLocationData() {
        try {
            // #region agent log
            // #endregion
            
            const apiUrl = CONFIG.getApiUrl('/location/active');
            
            if (!apiUrl) {
                throw new Error('API URL is not configured');
            }
            
            const response = await fetch(apiUrl, {
                credentials: 'include',
                headers: {
                    'Accept': 'application/json'
                }
            });
            
            // #region agent log
            // #endregion
            
            if (!response.ok) {
                const errorText = await response.text().catch(() => 'Unknown error');
                throw new Error(`HTTP error! status: ${response.status} - ${errorText}`);
            }
            
            const result = await response.json();
            
            // #region agent log
            // #endregion
            
            if (!result.success) {
                throw new Error(result.message || 'Failed to load location data');
            }
            
            if (!result.data) {
                throw new Error('Invalid response format - missing data');
            }
            
            this.locationData = result.data;
            this.locations = this.locationData.locations || [];
            this.backgroundImages = this.locationData.backgroundImages || [];
            
            // #region agent log
            // #endregion
            console.log('[DEBUG] Loaded background images:', this.backgroundImages);
            console.log('[DEBUG] Background images with useForLocationPage:', this.backgroundImages.filter(img => img.useForLocationPage === true));
        } catch (error) {
            console.error('Error loading location data:', error);
            // #region agent log
            // #endregion
            throw error;
        }
    }

    /**
     * WHAT: Setup background image rotation
     * WHY: Create dynamic visual experience
     * HOW: Timer-based image switching
     * IMPORTANCE: Visual appeal and location showcase
     */
    setupBackgroundRotation() {
        // If page is configured for static background, skip dynamic rotation
        if (document?.body?.dataset?.background === 'static') {
            return;
        }

        // Try both selectors - #location-background and .bg-image
        const bgImage = document.querySelector('#location-background') || document.querySelector('.bg-image');
        if (!bgImage) {
            console.warn('Location background image element not found');
            return;
        }

        // If no background images are available, use default background
        if (this.backgroundImages.length === 0) {
            // #region agent log
            // #endregion
            console.log('No background images available for rotation, using default');
            console.log('[DEBUG] Location data:', this.locationData);
            console.log('[DEBUG] Background images array:', this.backgroundImages);
            // Use default background image
            const defaultImagePath = CONFIG.environment === 'test' 
                ? "https://test.sourdoughies.com/images/backgrounds/cookies2.jpg" 
                : "/images/backgrounds/cookies2.jpg";
            
            // Remove transition to prevent flash
            const originalTransition = bgImage.style.transition;
            bgImage.style.transition = 'none';
            
            bgImage.style.display = 'block';
            bgImage.style.backgroundImage = `url('${defaultImagePath}')`;
            bgImage.style.visibility = 'visible';
            bgImage.style.opacity = '0.45'; /* Increased from 0.3 to 0.45 to match CSS and make background more visible */
            bgImage.classList.add('loaded');
            
            // Force reflow
            void bgImage.offsetHeight;
            
            // Restore transition
            bgImage.style.transition = originalTransition || '';
            return;
        }

        // Set initial background from location images
        this.setBackgroundImage(0);

        // Setup rotation interval
        const interval = (this.locationData.settings?.backgroundRotationInterval || 20) * 1000;
        this.backgroundInterval = setInterval(() => {
            this.currentBackgroundIndex = (this.currentBackgroundIndex + 1) % this.backgroundImages.length;
            this.setBackgroundImage(this.currentBackgroundIndex);
        }, interval);
    }

    /**
     * WHAT: Set specific background image
     * WHY: Display location-specific backgrounds
     * HOW: Update CSS background property
     * IMPORTANCE: Visual context for locations
     */
    setBackgroundImage(index) {
        // #region agent log
        // #endregion
        
        // Try both selectors - .bg-image and #location-background
        const bgImage = document.querySelector('#location-background') || document.querySelector('.bg-image');
        if (!bgImage || !this.backgroundImages[index]) {
            // #region agent log
            // #endregion
            return;
        }

        const image = this.backgroundImages[index];
        // Construct image URL - location images are stored in /uploads/location/
        let imageUrl = image.filename || image.url || image.imageUrl;
        
        // #region agent log
        // #endregion
        
        // Ensure proper URL format
        if (imageUrl && !imageUrl.startsWith('http') && !imageUrl.startsWith('/')) {
            imageUrl = `/uploads/location/${imageUrl}`;
        } else if (imageUrl && imageUrl.startsWith('/uploads/location/')) {
            // Already correct format
        } else if (image.filename && !imageUrl.includes('/')) {
            imageUrl = `/uploads/location/${image.filename}`;
        }
        
        // #region agent log
        // #endregion
        
        // Remove transition to prevent flash
        const originalTransition = bgImage.style.transition;
        bgImage.style.transition = 'none';
        
        // Set background image and visibility
        bgImage.style.display = 'block';
        bgImage.style.backgroundImage = `url('${imageUrl}')`;
        bgImage.style.visibility = 'visible';
        bgImage.style.opacity = '0.45'; /* Increased from 0.3 to 0.45 to match CSS and make background more visible */
        bgImage.classList.add('loaded');
        
        // #region agent log
        // #endregion
        
        // Force reflow
        void bgImage.offsetHeight;
        
        // Restore transition
        bgImage.style.transition = originalTransition || '';
    }

    /**
     * WHAT: Setup event listeners
     * WHY: Handle user interactions
     * HOW: DOM event binding
     * IMPORTANCE: Interactive user experience
     */
    setupEventListeners() {
        const filterBar = document.getElementById('location-filters');
        if (filterBar) {
            filterBar.querySelectorAll('.filter-btn').forEach((button) => {
                button.addEventListener('click', (e) => {
                    const filter = e.currentTarget.dataset.filter;
                    if (filter) this.handleFilterChange(filter);
                });
            });
        }

        // Map controls
        const centerMapBtn = document.getElementById('center-map');
        const showAllBtn = document.getElementById('show-all-locations');
        const showPermanentBtn = document.getElementById('show-permanent');
        const showEventsBtn = document.getElementById('show-events');
        
        if (centerMapBtn) {
            centerMapBtn.addEventListener('click', () => this.centerMap());
        }
        
        if (showAllBtn) {
            showAllBtn.addEventListener('click', () => this.showAllLocations());
        }

        if (showPermanentBtn) {
            showPermanentBtn.addEventListener('click', () => this.showPermanentLocations());
        }

        if (showEventsBtn) {
            showEventsBtn.addEventListener('click', () => this.showEventLocations());
        }

        // Location selector
        const locationSelect = document.getElementById('location-select');
        if (locationSelect) {
            locationSelect.addEventListener('change', (e) => {
                this.handleLocationSelection(e.target.value);
            });
        }

        // Modal close
        const modal = document.getElementById('location-modal');
        const closeBtn = modal?.querySelector('.close');
        
        if (closeBtn) {
            closeBtn.addEventListener('click', () => this.closeModal());
        }

        // Close modal on outside click
        if (modal) {
            modal.addEventListener('click', (e) => {
                if (e.target === modal) {
                    this.closeModal();
                }
            });
        }

        const grid = document.getElementById('location-grid');
        if (grid) {
            grid.addEventListener('click', (e) => this.handleLocationGridAction(e));
            grid.addEventListener('error', (e) => {
                const img = e.target;
                if (img instanceof HTMLImageElement && img.closest('.location__item-image')) {
                    img.onerror = null;
                    img.src = '/images/icons/no-image2.svg';
                }
            }, true);
        }
    }

    /**
     * WHAT: Handle location card action button clicks
     * WHY: Inline onclick handlers are stripped by DOMPurify and blocked by CSP
     * HOW: Event delegation on #location-grid using data-action attributes
     */
    handleLocationGridAction(e) {
        const button = e.target.closest('[data-action]');
        if (!button) return;

        const item = button.closest('.location__item');
        const locationId = item?.dataset.locationId;
        if (!locationId) return;

        const location = this.locations.find(
            (loc) => (loc._id || loc.id) === locationId
        );
        if (!location) return;

        const action = button.dataset.action;
        if (action === 'view-details') {
            this.showLocationDetails(locationId);
        } else if (action === 'get-directions') {
            const address = this.formatAddress(location.address);
            const lat = location.coordinates?.latitude || 0;
            const lng = location.coordinates?.longitude || 0;
            this.getDirections(lat, lng, address);
        } else if (action === 'visit-website') {
            this.visitWebsite(location.website);
        }
    }

    /**
     * WHAT: Handle location filter changes
     * WHY: Filter location display by type
     * HOW: Update filter state and re-render
     * IMPORTANCE: User navigation and content organization
     */
    /**
     * Sync active state on location filter buttons (scoped to #location-filters).
     */
    syncFilterControl(filter) {
        const filterBar = document.getElementById('location-filters');
        if (!filterBar) return;
        filterBar.querySelectorAll('.filter-btn').forEach((btn) => {
            btn.classList.toggle('active', btn.dataset.filter === filter);
        });
    }

    handleFilterChange(filter) {
        this.currentFilter = filter;
        this.syncFilterControl(filter);
        this.renderLocationGrid();
    }

    /**
     * WHAT: Render overview cards
     * WHY: Show location statistics and quick info
     * HOW: Update DOM with calculated stats and location lists
     * IMPORTANCE: Quick location overview
     */
    renderOverviewCards() {
        if (!this.locations || this.locations.length === 0) return;

        const locations = this.locations;
        const permanentLocations = locations.filter(loc => loc.type === 'permanent' && loc.status === 'active');
        const eventLocations = locations.filter(loc => loc.type === 'event' && loc.status === 'active');
        const otherLocations = locations.filter(loc => loc.type === 'other' && loc.status === 'active');
        const upcomingEvents = this.locationData.upcomingEvents || [];

        // Update permanent locations card
        const permanentCount = document.getElementById('permanent-count');
        const permanentLocationsList = document.getElementById('permanent-locations');
        
        if (permanentCount) {
            permanentCount.textContent = permanentLocations.length;
        }
        
        if (permanentLocationsList) {
            permanentLocationsList.innerHTML = permanentLocations
                .slice(0, 3) // Show first 3
                .map(loc => {
                    // WHAT: Sanitize location data before inserting into DOM
                    // WHY: Prevent XSS attacks from malicious location data
                    // HOW: Use DOMPurify to sanitize each field
                    const sanitizedName = DOMPurify.sanitize(loc.name || '');
                    const sanitizedAddress = DOMPurify.sanitize(loc.address || '');
                    return `
                        <div class="overview-location-item">
                            <div class="overview-location-name">${sanitizedName}</div>
                            <div class="overview-location-address">${sanitizedAddress}</div>
                        </div>
                    `;
                }).join('');
        }

        // Update event locations card
        const eventCount = document.getElementById('event-count');
        const eventLocationsList = document.getElementById('event-locations');
        
        if (eventCount) {
            eventCount.textContent = eventLocations.length;
        }
        
        if (eventLocationsList) {
            eventLocationsList.innerHTML = eventLocations
                .slice(0, 3) // Show first 3
                .map(loc => {
                    // WHAT: Sanitize location data before inserting into DOM
                    // WHY: Prevent XSS attacks from malicious location data
                    // HOW: Use DOMPurify to sanitize each field
                    const sanitizedName = DOMPurify.sanitize(loc.name || '');
                    const sanitizedAddress = DOMPurify.sanitize(loc.address || '');
                    return `
                        <div class="overview-location-item">
                            <div class="overview-location-name">${sanitizedName}</div>
                            <div class="overview-location-address">${sanitizedAddress}</div>
                        </div>
                    `;
                }).join('');
        }

        // Update upcoming events card
        const upcomingCount = document.getElementById('upcoming-count');
        const upcomingEventsList = document.getElementById('upcoming-events');
        
        if (upcomingCount) {
            upcomingCount.textContent = upcomingEvents.length;
        }

        // Update other locations card
        const otherCount = document.getElementById('other-count');
        const otherLocationsList = document.getElementById('other-locations');
        if (otherCount) {
            otherCount.textContent = otherLocations.length;
        }
        if (otherLocationsList) {
            otherLocationsList.innerHTML = otherLocations
                .slice(0, 3)
                .map(loc => {
                    // WHAT: Sanitize location data before inserting into DOM
                    // WHY: Prevent XSS attacks from malicious location data
                    // HOW: Use DOMPurify to sanitize each field
                    const sanitizedName = DOMPurify.sanitize(loc.name || '');
                    const sanitizedAddress = DOMPurify.sanitize(loc.address || '');
                    return `
                        <div class="overview-location-item">
                            <div class="overview-location-name">${sanitizedName}</div>
                            <div class="overview-location-address">${sanitizedAddress}</div>
                        </div>
                    `;
                }).join('');
        }
        
        if (upcomingEventsList) {
            upcomingEventsList.innerHTML = upcomingEvents
                .slice(0, 3) // Show first 3
                .map(event => {
                    // WHAT: Sanitize event data before inserting into DOM
                    // WHY: Prevent XSS attacks from malicious event data
                    // HOW: Use DOMPurify to sanitize each field
                    const sanitizedName = DOMPurify.sanitize(event.name || '');
                    const eventDate = event.date ? new Date(event.date).toLocaleDateString() : 'Date TBD';
                    const sanitizedDate = DOMPurify.sanitize(eventDate);
                    return `
                        <div class="overview-event-item">
                            <div class="overview-event-name">${sanitizedName}</div>
                            <div class="overview-event-date">${sanitizedDate}</div>
                        </div>
                    `;
                }).join('');
        }
    }

    /**
     * WHAT: Setup location selector dropdown
     * WHY: Allow users to select specific locations
     * HOW: Populate select element with location options
     * IMPORTANCE: Location-specific information display
     */
    setupLocationSelector() {
        if (!this.locationData?.locations) return;

        const locationSelect = document.getElementById('location-select');
        if (!locationSelect) return;

        // Clear existing options and add placeholder
        locationSelect.innerHTML = '';
        const placeholderOption = document.createElement('option');
        placeholderOption.value = '';
        placeholderOption.textContent = 'Choose a location to view details...';
        locationSelect.appendChild(placeholderOption);

        // Add location options
        this.locations
            .filter(loc => loc.status === 'active')
            .forEach(location => {
                const option = document.createElement('option');
                option.value = location._id || location.id;
                option.textContent = `${location.name} - ${location.address}`;
                locationSelect.appendChild(option);
            });
    }

    /**
     * WHAT: Handle location selection
     * WHY: Update business info based on selected location
     * HOW: Find location data and update display
     * IMPORTANCE: Dynamic content based on user selection
     */
    handleLocationSelection(locationId) {
        if (!locationId) {
            this.selectedLocation = null;
            this.updateBusinessInfo();
            return;
        }

        this.selectedLocation = this.locations.find(
            loc => (loc._id || loc.id) === locationId
        );

        if (this.selectedLocation) {
            this.updateBusinessInfo();
            this.centerMapOnLocation(this.selectedLocation);
        }
    }

    async loadHoursDisplayMeta() {
        try {
            const url = CONFIG.getApiUrl('/menu/public-mode');
            if (!url) return;
            const response = await fetch(url, { credentials: 'include' });
            if (!response.ok) return;
            const result = await response.json();
            const d = result.data || result;
            this.hoursMeta = {
                hoursMode: d.hoursMode,
                hoursDataSource: d.hoursDataSource,
                lastSquareHoursSyncedAt: d.lastSquareHoursSyncedAt
            };
        } catch {
            this.hoursMeta = null;
        }
    }

    formatHoursSourceNote() {
        if (!this.hoursMeta) return '';
        const mode = this.hoursMeta.hoursMode === 'automatic' ? 'Square (automatic sync)' : 'Admin (manual)';
        const last = this.hoursMeta.lastSquareHoursSyncedAt;
        const lastStr = last
            ? new Date(last).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })
            : null;
        const parts = [`Hours source: ${mode}`];
        if (this.hoursMeta.hoursDataSource) parts.push(this.hoursMeta.hoursDataSource);
        if (lastStr) parts.push(`Last Square pull: ${lastStr}`);
        return `<p class="hours-source-note" style="font-size:0.85em;opacity:0.85;margin:0 0 0.75rem 0;">${DOMPurify.sanitize(parts.join(' · '))}</p>`;
    }

    /**
     * WHAT: Update business information display
     * WHY: Show location-specific business hours and contact info
     * HOW: Update DOM with selected location data
     * IMPORTANCE: Relevant information for selected location
     */
    updateBusinessInfo() {
        const hoursDisplay = document.getElementById('hours-display');
        const contactInfo = document.getElementById('contact-info');

        if (!this.selectedLocation) {
            // Show placeholder content
            if (hoursDisplay) {
                hoursDisplay.innerHTML = '';
                const hoursP = document.createElement('p');
                hoursP.className = 'no-selection';
                hoursP.textContent = 'Select a location to view business hours';
                hoursDisplay.appendChild(hoursP);
            }
            if (contactInfo) {
                contactInfo.innerHTML = '';
                const contactP = document.createElement('p');
                contactP.className = 'no-selection';
                contactP.textContent = 'Select a location to view contact information';
                contactInfo.appendChild(contactP);
            }
            return;
        }

        // Update business hours
        if (hoursDisplay) {
            hoursDisplay.innerHTML = '';
            const sourceNote = this.formatHoursSourceNote();
            if (sourceNote) {
                hoursDisplay.insertAdjacentHTML('afterbegin', sourceNote);
            }
            if (this.selectedLocation.businessHours) {
                const hoursContent = this.formatBusinessHours(this.selectedLocation.businessHours);
                hoursDisplay.insertAdjacentHTML('beforeend', DOMPurify.sanitize(hoursContent));
            } else {
                const noHoursP = document.createElement('p');
                noHoursP.className = 'no-selection';
                noHoursP.textContent = 'Business hours not available';
                hoursDisplay.appendChild(noHoursP);
            }
        }

        // Update contact information
        if (contactInfo) {
            const contactHtml = this.formatContactInfo(this.selectedLocation);
            contactInfo.innerHTML = DOMPurify.sanitize(contactHtml);
        }
    }

    /**
     * WHAT: Format business hours for display
     * WHY: Present hours in readable format
     * HOW: Convert hours object to HTML
     * IMPORTANCE: Clear business hours presentation
     */
    formatBusinessHours(hours) {
        if (!hours || typeof hours !== 'object') {
            return '<p class="no-selection">Business hours not available</p>';
        }

        const daysOfWeek = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
        let hoursHtml = '';

        daysOfWeek.forEach(day => {
            const dayHours = hours[day.toLowerCase()];
            if (dayHours) {
                let timeDisplay;
                if (dayHours.closed) {
                    timeDisplay = 'Closed';
                } else if (dayHours.open && dayHours.close) {
                    timeDisplay = `${this.convertToAMPM(dayHours.open)} - ${this.convertToAMPM(dayHours.close)}`;
                } else {
                    timeDisplay = 'Hours not set';
                }
                
                hoursHtml += `
                    <div>
                        <div class="hours-day">${day}</div>
                        <div class="hours-time">${timeDisplay}</div>
                    </div>
                `;
            }
        });

        return hoursHtml || '<p class="no-selection">Business hours not available</p>';
    }

    formatBusinessHoursHorizontal(hours) {
        if (!hours || typeof hours !== 'object') {
            return '<p class="no-selection">Business hours not available</p>';
        }

        const daysOfWeek = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
        let hoursHtml = '<div class="location-card__hours-grid">';

        daysOfWeek.forEach(day => {
            const dayHours = hours[day.toLowerCase()];
            if (dayHours) {
                let timeDisplay;
                if (dayHours.closed) {
                    timeDisplay = 'Closed';
                } else if (dayHours.open && dayHours.close) {
                    timeDisplay = `${this.convertToAMPM(dayHours.open)} - ${this.convertToAMPM(dayHours.close)}`;
                } else {
                    timeDisplay = 'Hours not set';
                }
                
                hoursHtml += `
                    <div class="location-card__hours-item">
                        <div class="location-card__hours-day">${day}</div>
                        <div class="location-card__hours-time">${timeDisplay}</div>
                    </div>
                `;
            }
        });

        hoursHtml += '</div>';
        return hoursHtml || '<p class="no-selection">No business hours available</p>';
    }

    convertToAMPM(time24) {
        if (!time24 || typeof time24 !== 'string') return time24;
        
        // Handle formats like "07:00" or "19:00"
        const timeMatch = time24.match(/^(\d{1,2}):(\d{2})$/);
        if (!timeMatch) return time24;
        
        let hours = parseInt(timeMatch[1], 10);
        const minutes = timeMatch[2];
        
        if (hours === 0) {
            return `12:${minutes} AM`;
        } else if (hours < 12) {
            return `${hours}:${minutes} AM`;
        } else if (hours === 12) {
            return `12:${minutes} PM`;
        } else {
            return `${hours - 12}:${minutes} PM`;
        }
    }

    formatBusinessHoursUserFriendly(hours) {
        if (!hours || typeof hours !== 'object') {
            return '<p class="no-selection">Business hours not available</p>';
        }

        const daysOfWeek = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
        let hoursHtml = '<div class="hours-grid">';

        daysOfWeek.forEach(day => {
            const dayHours = hours[day.toLowerCase()];
            if (dayHours) {
                let timeDisplay;
                if (dayHours.closed) {
                    timeDisplay = 'Closed';
                } else if (dayHours.open && dayHours.close) {
                    timeDisplay = `${this.convertToAMPM(dayHours.open)} - ${this.convertToAMPM(dayHours.close)}`;
                } else {
                    timeDisplay = 'Hours not set';
                }
                
                hoursHtml += `
                    <div class="hours-item">
                        <span class="hours-day">${day}:</span>
                        <span class="hours-time">${timeDisplay}</span>
                    </div>
                `;
            }
        });

        hoursHtml += '</div>';
        return hoursHtml || '<p class="no-selection">No business hours available</p>';
    }

    /**
     * WHAT: Format address for display
     * WHY: Present address in readable format
     * HOW: Convert address object to string
     * IMPORTANCE: Clear address presentation
     */
    formatAddress(address) {
        if (!address || typeof address !== 'object') {
            return 'Address not available';
        }

        const parts = [];
        if (address.street) parts.push(address.street);
        if (address.city) parts.push(address.city);
        if (address.state) parts.push(address.state);
        if (address.zipCode) parts.push(address.zipCode);

        return parts.length > 0 ? parts.join(', ') : 'Address not available';
    }

    /**
     * WHAT: Format contact information for display
     * WHY: Present contact info in readable format
     * HOW: Convert contact object to HTML
     * IMPORTANCE: Clear contact information presentation
     */
    formatContactInfo(location) {
        let contactHtml = '';

        if (location.phone) {
            contactHtml += `
                <div>
                    <div class="contact-label">Phone</div>
                    <div class="contact-value">
                        <a href="tel:${location.phone}">${location.phone}</a>
                    </div>
                </div>
            `;
        }

        if (location.email) {
            contactHtml += `
                <div>
                    <div class="contact-label">Email</div>
                    <div class="contact-value">
                        <a href="mailto:${location.email}">${location.email}</a>
                    </div>
                </div>
            `;
        }

        if (location.address) {
            // WHAT: Format address with colon for consistency with other contact fields
            // WHY: Match the styling of other fields in the details modal
            // HOW: Add colon to label and sanitize address value
            const formattedAddress = this.formatAddress(location.address);
            const sanitizedAddress = DOMPurify.sanitize(formattedAddress);
            contactHtml += `
                <div>
                    <div class="contact-label">Address:</div>
                    <div class="contact-value">${sanitizedAddress}</div>
                </div>
            `;
        }

        if (location.website) {
            // WHAT: Sanitize website URL before inserting into DOM
            // WHY: Prevent XSS attacks from malicious URLs
            // HOW: Use DOMPurify to sanitize the URL
            const sanitizedWebsite = DOMPurify.sanitize(location.website);
            contactHtml += `
                <div>
                    <div class="contact-label">Website</div>
                    <div class="contact-value">
                        <a href="${sanitizedWebsite}" target="_blank" rel="noopener noreferrer">${sanitizedWebsite}</a>
                    </div>
                </div>
            `;
        }

        return contactHtml || '<p class="no-selection">Contact information not available</p>';
    }

    /**
     * WHAT: Render location grid
     * WHY: Display all locations in grid format
     * HOW: Filter and render location cards
     * IMPORTANCE: Complete location listing
     */
    renderLocationGrid() {
        if (!this.locations || this.locations.length === 0) return;

        const grid = document.getElementById('location-grid');
        if (!grid) return;

        let locations = this.locations.filter(loc => loc.status === 'active');

        // Apply filter
        if (this.currentFilter !== 'all') {
            locations = locations.filter(loc => loc.type === this.currentFilter);
        }

        if (locations.length === 0) {
            grid.innerHTML = '<p class="no-selection">No locations found for the selected filter.</p>';
            return;
        }

        // WHAT: Sanitize the entire HTML string after joining location cards
        // WHY: Even though individual fields are sanitized, sanitize the whole HTML for defense-in-depth
        // HOW: Use DOMPurify to sanitize the complete HTML string
        const locationCardsHtml = locations.map(location => this.createLocationCard(location)).join('');
        grid.innerHTML = DOMPurify.sanitize(locationCardsHtml);
    }

    /**
     * WHAT: Create location card HTMLs
     * WHY: Generate consistent location card display
     * HOW: Template string with location data
     * IMPORTANCE: Consistent card presentation
     */
    createLocationCard(location) {
        // WHAT: Sanitize all user-controlled data before inserting into DOM
        // WHY: Prevent XSS attacks from malicious location data
        // HOW: Use DOMPurify to sanitize each field individually
        const sanitizedId = DOMPurify.sanitize(location._id || location.id || '');
        const sanitizedName = DOMPurify.sanitize(location.name || 'Unnamed Location');
        const sanitizedType = DOMPurify.sanitize(location.type || '');
        const formattedType = sanitizedType ? sanitizedType.charAt(0).toUpperCase() + sanitizedType.slice(1) : 'N/A';
        
        const hours = location.businessHours ? this.formatBusinessHoursUserFriendly(location.businessHours) : 'Hours not available';
        const sanitizedHours = DOMPurify.sanitize(hours);
        const address = this.formatAddress(location.address);
        const sanitizedAddress = DOMPurify.sanitize(address);
        
        // Use location image if available, otherwise use no-image SVG
        let imageUrl = '/images/icons/no-image2.svg'; // No-image fallback
        if (location.imageUrl && location.imageUrl.trim() !== '') {
            imageUrl = location.imageUrl;
        }
        // WHAT: Sanitize image URL - DOMPurify handles URLs safely
        // WHY: Prevent malicious URLs or script injection via image src
        const sanitizedImageUrl = DOMPurify.sanitize(imageUrl);
        const sanitizedImageAlt = sanitizedName || 'Location';
        
        // Add cache buster to image URL
        const imageUrlWithCache = sanitizedImageUrl.includes('?') ? sanitizedImageUrl : `${sanitizedImageUrl}?v=${Date.now()}`;

        const websiteButton = location.website && location.website.trim() !== ''
            ? '<button type="button" class="btn btn-secondary" data-action="visit-website">Visit Website</button>'
            : '';

        return `<div class="location__item" data-location-id="${sanitizedId}" data-location-type="${sanitizedType}"><div class="location__item-image"><img src="${imageUrlWithCache}" alt="${sanitizedImageAlt}"></div><div class="location__item-content"><h3 class="location__item-title">${sanitizedName}</h3><div class="location__item-type">Type: ${formattedType}</div><div class="location__item-address">${sanitizedAddress}</div><div class="location__item-hours">${sanitizedHours}</div><div class="location__item-actions"><button type="button" class="btn btn-primary" data-action="view-details">View Details</button><button type="button" class="btn btn-secondary" data-action="get-directions">Get Directions</button>${websiteButton}</div></div></div>`;
    }

    /**
     * WHAT: Setup interactive map
     * WHY: Visual location representation
     * HOW: Initialize map with location markers
     * IMPORTANCE: Visual location context
     */
    setupMap() {
        const mapElement = document.getElementById('map');
        if (!mapElement) return;

        // Placeholder for map implementation
        // In a real implementation, you would integrate with Google Maps, Mapbox, or similar
        mapElement.innerHTML = `
            <div style="display: flex; align-items: center; justify-content: center; height: 100%;">
                <div style="text-align: center;">
                    <div style="font-size: 2rem; margin-bottom: 1rem;">🗺️</div>
                    <div>Interactive Map</div>
                    <div style="font-size: 0.9rem; opacity: 0.8; margin-top: 0.5rem;">
                        Map integration coming soon
                    </div>
                </div>
            </div>
        `;
    }

    /**
     * WHAT: Center map on specific location
     * WHY: Focus map on selected location
     * HOW: Update map center coordinates
     * IMPORTANCE: Visual location focus
     */
    centerMapOnLocation(location) {
        if (!location.coordinates) return;
        
        console.log(`Centering map on ${location.name} at ${location.coordinates.lat}, ${location.coordinates.lng}`);
        // Map centering logic would go here
    }

    /**
     * WHAT: Center map on all locations
     * WHY: Show overview of all locations
     * HOW: Calculate map bounds
     * IMPORTANCE: Overall location context
     */
    centerMap() {
        console.log('Centering map on all locations');
        // Map centering logic would go here
    }

    /**
     * WHAT: Show all locations on map
     * WHY: Display complete location overview
     * HOW: Add all location markers
     * IMPORTANCE: Complete location visualization
     */
    showAllLocations() {
        console.log('Showing all locations on map');
        // Show all locations logic would go here
    }

    /**
     * WHAT: Show only permanent locations on map
     * WHY: Filter map to permanent locations
     * HOW: Filter and display permanent location markers
     * IMPORTANCE: Permanent location focus
     */
    showPermanentLocations() {
        console.log('Showing permanent locations on map');
        // Show permanent locations logic would go here
    }

    /**
     * WHAT: Show only event locations on map
     * WHY: Filter map to event locations
     * HOW: Filter and display event location markers
     * IMPORTANCE: Event location focus
     */
    showEventLocations() {
        console.log('Showing event locations on map');
        // Show event locations logic would go here
    }

    /**
     * WHAT: View all locations of a specific type
     * WHY: Filter and show all locations of selected type
     * HOW: Update filter and scroll to location grid
     * IMPORTANCE: Easy navigation to specific location types
     */
    viewAllLocations(type) {
        this.currentFilter = type;
        this.syncFilterControl(type);

        // Re-render the location grid
        this.renderLocationGrid();
        
        // Scroll to the location grid section
        const locationGrid = document.getElementById('location-grid');
        if (locationGrid) {
            locationGrid.scrollIntoView({ 
                behavior: 'smooth', 
                block: 'start' 
            });
        }
        
        // Update map to show only this type
        if (type === 'permanent') {
            this.showPermanentLocations();
        } else if (type === 'event') {
            this.showEventLocations();
        }
    }

    /**
     * WHAT: View all upcoming events
     * WHY: Show all upcoming events in a dedicated view
     * HOW: Create and display events modal or section
     * IMPORTANCE: Complete events information
     */
    viewAllEvents() {
        const upcomingEvents = this.locationData.upcomingEvents || [];
        
        if (upcomingEvents.length === 0) {
            alert('No upcoming events available at this time.');
            return;
        }
        
        // Create modal content for all events
        const modal = document.getElementById('location-modal');
        const modalContent = document.getElementById('modal-content');
        
        if (!modal || !modalContent) return;
        
        // WHAT: Sanitize all event data before inserting into DOM
        // WHY: Prevent XSS attacks from malicious event data
        // HOW: Use DOMPurify to sanitize each field individually
        const eventsHtml = upcomingEvents.map(event => {
            const sanitizedName = DOMPurify.sanitize(event.name || 'Unnamed Event');
            const eventDate = event.date ? new Date(event.date).toLocaleDateString() : 'Date TBD';
            const sanitizedDate = DOMPurify.sanitize(eventDate);
            const sanitizedLocation = event.location ? DOMPurify.sanitize(event.location) : null;
            const sanitizedDescription = event.description ? DOMPurify.sanitize(event.description) : null;
            const sanitizedTime = event.time ? DOMPurify.sanitize(event.time) : null;
            
            return `
                <div style="margin-bottom: 2rem; padding: 1rem; border: 1px solid rgba(255, 255, 255, 0.2); border-radius: 8px;">
                    <h3 style="color: var(--primary-color); margin-bottom: 0.5rem;">${sanitizedName}</h3>
                    <div style="margin-bottom: 0.5rem;">
                        <strong>Date:</strong> ${sanitizedDate}
                    </div>
                    ${sanitizedLocation ? `<div style="margin-bottom: 0.5rem;"><strong>Location:</strong> ${sanitizedLocation}</div>` : ''}
                    ${sanitizedDescription ? `<div style="margin-bottom: 0.5rem;"><strong>Description:</strong> ${sanitizedDescription}</div>` : ''}
                    ${sanitizedTime ? `<div><strong>Time:</strong> ${sanitizedTime}</div>` : ''}
                </div>
            `;
        }).join('');
        
        modalContent.innerHTML = DOMPurify.sanitize(`
            <h2 style="color: var(--primary-color); margin-bottom: 2rem;">All Upcoming Events</h2>
            ${eventsHtml}
        `);
        
        modal.style.display = 'block';
    }

    /**
     * WHAT: Show location details in modal
     * WHY: Display detailed location information
     * HOW: Open modal with location data
     * IMPORTANCE: Detailed location information
     */
    showLocationDetails(locationId) {
        const location = this.locations.find(
            loc => (loc._id || loc.id) === locationId
        );

        if (!location) return;

        const modal = document.getElementById('location-modal');
        const modalContent = document.querySelector('#location-modal .modal-content');

        if (!modal || !modalContent) return;

        const detailedHours = this.formatDetailedHours(location.businessHours);
        const contactInfo = this.formatContactInfo(location);

        // Get location image URL
        let imageUrl = '/images/icons/Logo no background jpeg oopsies cant find it image 1024 840(1).svg';
        if (location.imageUrl && location.imageUrl.trim() !== '') {
            imageUrl = location.imageUrl;
        }

        // Set background image with dark overlay for text readability
        modalContent.style.backgroundImage = `linear-gradient(rgba(0, 0, 0, 0.85), rgba(0, 0, 0, 0.95)), url('${imageUrl}')`;
        modalContent.style.backgroundSize = 'cover';
        modalContent.style.backgroundPosition = 'center';
        modalContent.style.backgroundRepeat = 'no-repeat';

        const contentDiv = document.getElementById('modal-content');
        // WHAT: Sanitize all location data before inserting into DOM
        // WHY: Prevent XSS attacks from malicious location data
        // HOW: Use DOMPurify to sanitize each field individually
        const sanitizedName = DOMPurify.sanitize(location.name || 'Unnamed Location');
        const sanitizedType = DOMPurify.sanitize(location.type || 'N/A');
        const formattedAddress = this.formatAddress(location.address);
        const sanitizedAddress = DOMPurify.sanitize(formattedAddress);
        // WHAT: Sanitize contactInfo HTML before insertion
        // WHY: Ensure all HTML content is safe, even though individual values are sanitized
        // HOW: Use DOMPurify to sanitize the entire HTML string, allowing anchor tags
        // NOTE: DOMPurify allows anchor tags by default, but we ensure href and target attributes are preserved
        const sanitizedContactInfo = DOMPurify.sanitize(contactInfo, {
            ALLOWED_TAGS: ['div', 'a', 'p'],
            ALLOWED_ATTR: ['href', 'target', 'rel', 'class', 'style']
        });
        const sanitizedDetailedHours = DOMPurify.sanitize(detailedHours);
        const specialHoursText = (location.specialHours || '').trim();
        const sanitizedSpecialHours = specialHoursText
            ? DOMPurify.sanitize(specialHoursText)
            : null;
        const sanitizedDescription = location.description ? DOMPurify.sanitize(location.description) : null;
        
        // WHAT: Sanitize the entire HTML template string
        // WHY: Even though individual values are sanitized, sanitize the whole HTML for defense-in-depth
        // HOW: Use DOMPurify to sanitize the complete HTML string
        const contentHtml = `
            <h2>${sanitizedName}</h2>
            <div style="margin-bottom: 1rem;">
                <strong>Type:</strong> ${sanitizedType}
            </div>
            <div style="margin-bottom: 1rem;">
                <strong>Business Hours:</strong>
                ${sanitizedDetailedHours}
            </div>
            ${sanitizedSpecialHours ? `<div style="margin-bottom: 1rem;"><strong>Special Hours:</strong> ${sanitizedSpecialHours}</div>` : ''}
            <div style="margin-bottom: 1rem;">
                <strong>Contact Information:</strong>
                ${sanitizedContactInfo}
            </div>
            ${sanitizedDescription ? `<div style="margin-bottom: 1rem;"><strong>Description:</strong> ${sanitizedDescription}</div>` : ''}
        `;
        contentDiv.innerHTML = DOMPurify.sanitize(contentHtml);

        modal.style.display = 'block';
    }

    /**
     * WHAT: Format detailed business hours
     * WHY: Show comprehensive hours information
     * HOW: Convert hours object to detailed HTML
     * IMPORTANCE: Complete hours information
     */
    formatDetailedHours(hours) {
        if (!hours || typeof hours !== 'object') {
            return 'Business hours not available';
        }

        const daysOfWeek = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
        let hoursHtml = '<div style="margin-top: 0.5rem;">';

        daysOfWeek.forEach(day => {
            const dayHours = hours[day.toLowerCase()];
            if (dayHours) {
                let timeDisplay;
                if (dayHours.closed) {
                    timeDisplay = 'Closed';
                } else if (dayHours.open && dayHours.close) {
                    timeDisplay = `${dayHours.open} - ${dayHours.close}`;
                } else {
                    timeDisplay = 'Hours not set';
                }
                
                hoursHtml += `
                    <div style="margin-bottom: 0.5rem;">
                        <strong>${day}:</strong> ${timeDisplay}
                    </div>
                `;
            }
        });

        hoursHtml += '</div>';
        return hoursHtml;
    }

    /**
     * WHAT: Close modal
     * WHY: Hide location details modal
     * HOW: Hide modal element
     * IMPORTANCE: Modal interaction
     */
    closeModal() {
        const modal = document.getElementById('location-modal');
        if (modal) {
            modal.style.display = 'none';
        }
    }

    /**
     * WHAT: Get directions to location
     * WHY: Help users navigate to location using their device's default navigation
     * HOW: Open Google Maps with the address - device will handle the rest
     * IMPORTANCE: Simple, universal navigation solution
     */
    getDirections(lat, lng, address = '') {
        let query = '';
        if (address && address.trim()) {
            query = encodeURIComponent(address.trim());
        } else if (lat && lng) {
            query = encodeURIComponent(`${lat},${lng}`);
        } else {
            return;
        }

        const url = `https://www.google.com/maps/search/?api=1&query=${query}`;
        window.open(url, '_blank', 'noopener,noreferrer');
    }

    /**
     * WHAT: Visit location website
     * WHY: Allow users to navigate to the location's website
     * HOW: Open website URL in new tab
     * IMPORTANCE: Direct access to location website
     */
    visitWebsite(websiteUrl) {
        // WHAT: Validate and sanitize website URL before opening
        // WHY: Prevent XSS attacks and ensure URL is safe
        // HOW: Use DOMPurify to sanitize, then validate it's a proper URL
        if (!websiteUrl) {
            console.error('No website URL provided');
            return;
        }

        let sanitizedUrl = DOMPurify.sanitize(websiteUrl.trim());
        if (!/^https?:\/\//i.test(sanitizedUrl)) {
            sanitizedUrl = `https://${sanitizedUrl}`;
        }

        try {
            const parsed = new URL(sanitizedUrl);
            if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') {
                console.error('Invalid website URL protocol');
                return;
            }
            window.open(parsed.href, '_blank', 'noopener,noreferrer');
        } catch {
            console.error('Invalid website URL format');
        }
    }

    /**
     * WHAT: Show error message
     * WHY: Inform users of errors
     * HOW: Display error in UI
     * IMPORTANCE: User feedback
     */
    showError(message) {
        const grid = document.getElementById('location-grid');
        if (grid) {
            // WHAT: Sanitize error message before inserting into DOM
            // WHY: Prevent XSS attacks from malicious error messages
            // HOW: Use DOMPurify to sanitize the message
            const sanitizedMessage = DOMPurify.sanitize(message || 'An error occurred');
            const errorHtml = `
                <div style="text-align: center; padding: 2rem; color: var(--primary-color);">
                    <div style="font-size: 2rem; margin-bottom: 1rem;">⚠️</div>
                    <p>${sanitizedMessage}</p>
                </div>
            `;
            grid.innerHTML = DOMPurify.sanitize(errorHtml);
        }
    }

    /**
     * WHAT: Cleanup resources
     * WHY: Prevent memory leaks
     * HOW: Clear intervals and remove listeners
     * IMPORTANCE: Resource management
     */
    destroy() {
        if (this.backgroundInterval) {
            clearInterval(this.backgroundInterval);
        }
    }
}

// Initialize location page manager when DOM is ready
let locationManager = null;

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
        locationManager = new LocationPageManager();
        window.locationManager = locationManager;
    });
} else {
    // DOM is already ready
    locationManager = new LocationPageManager();
    window.locationManager = locationManager;
}

// Cleanup on page unload
window.addEventListener('beforeunload', () => {
    if (locationManager) {
        locationManager.destroy();
    }
}); 